import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Routes, Route, useLocation } from 'react-router-dom';
import SimpleThemeProvider from './components/SimpleThemeProvider';
import PropTypes from 'prop-types';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Footer from './components/Footer/Footer';
import Header from './components/Header/Header';
import { SchemaProvider } from './context/shemaContext';
import { CartContextProvider } from './context/CartContext';
import { UserProvider } from './context/UserContext';
import { ListingContextProvider } from './context/ListingContext';
import { ContextProvider } from './context/store';
import { QuarticonContextProvider } from './context/QuarticonContext';
import { CompareProvider } from './hooks/useCompare';
import { PopupProvider } from './context/PopupContext';
import { readBackendConfig } from './utils/readBackendConfig';
import HeadMetaTags from './components/HeadMetaTags';
import { GET_BACKEND_CONFIG } from './commonQueries';
import loadable from './components/loadable';
import { deityGreenTheme, globalCss } from './theme';
import { ThemeEditorState } from './lib/@deity-io/falcon-theme-editor';
import HealthCheckRoute from './routes/HealthCheckRoute';
import PreConnect from './components/Header/PreConnect';
import ScrollToTop from './components/ScrollToTop';
import NewsletterSlider from './components/NewsletterSlider/NewsletterSlider';
import { OnlineStatus } from './components/OnlineStatus';
import { ErrorBoundary } from './components/ErrorBoundary';
import PageTransitionWrapper from './components/PageTransitionWrapper';
import I18n from './utils/I18n/I18n';
import Media from './screens/Media';
import DynamicRoute from './routes/DynamicRoute';
import {I18nextProvider, useTranslation} from 'react-i18next';
import CompareBlock from "./components/Compare/CompareBlock";
import { HelmetProvider } from 'react-helmet-async';
import './App.scss';
import Compare from "./components/Compare/Compare";
import SearchResults from "./screens/SearchResults/SearchResults";
import CheckoutPaymentSuccess from "./screens/Checkout/CheckoutPaymentSuccess";
import CheckoutPaymentCancel from "./screens/Checkout/CheckoutPaymentCancel";
import NotFoundPage from "./routes/NotFoundPage/NotFoundPage";
import SalesAndDiscounts from "./screens/SalesAndDiscounts/SalesAndDiscounts";
import Blog from "./screens/Blog/Blog";
import BlogDetails from "./screens/Blog/BlogDetails/BlogDetails";
import News from "./screens/News/News";
import NewsDetails from "./screens/News/NewsDetails/NewsDetails";
import Cart from "./screens/Cart/Cart";
import Checkout from "./screens/Checkout/Checkout";
import CheckoutSuccess from "./screens/CheckoutSuccess";
import Registration from "./screens/Registration/Registration";
import Login from "./screens/Login/Login";
import RequestReset from "./screens/Login/PasswordReset/RequestReset";
import ResetPassword from "./screens/Login/PasswordReset/ResetPassword";
import LogoutSuccess from "./screens/Logout/LogoutSuccess";
import SubscriberConfirm from "./screens/Newsletter/SubscriberConfirm";
import PrivateRoute from './utils/privateRoute';
import PersonalArea from "./screens/PersonalAreaScreen/PersonalArea";
import Delete from "./screens/Login/Delete";
import ProductAlertSubscribe from "./components/ProductAlert/ProductAlertSubscribe";
import ProductAlertUnsubscribe from "./components/ProductAlert/ProductAlertUnsubscribe";
import ProductAlertUnsubscribeAll from "./components/ProductAlert/ProductAlertUnsubscribeAll";
import Wishlist from "./components/Wishlist/Wishlist";
import i18n from "./i18n";
import TagManager from 'react-gtm-module'
import CookieDeclaration from "components/CookieDeclaration";
const ServiceWorkerRegistration = loadable(() => import('./utils/registerServiceWorker'), { ssr: false });

const GlobalProviders = ({ children }) => {
    const { data: backendConfig, loading: backendConfigLoading, error: backendConfigError } = useQuery(GET_BACKEND_CONFIG);

    const config = useMemo(
        () => readBackendConfig(backendConfig, backendConfigLoading),
        [backendConfig, backendConfigLoading]
    );

    if (backendConfigError) {
        console.error('Error loading backend config:', backendConfigError);
        return null; // Możesz dodać fallback UI
    }

    return (
        <HelmetProvider>
            <SchemaProvider>
                <I18nextProvider i18n={i18n}>
                    <GoogleOAuthProvider clientId={config.google_client_id}>
                        <CartContextProvider>
                            <UserProvider>
                                <ListingContextProvider>
                                    <ContextProvider>
                                        <QuarticonContextProvider>
                                            <CompareProvider>
                                                <PopupProvider>{children}</PopupProvider>
                                            </CompareProvider>
                                        </QuarticonContextProvider>
                                    </ContextProvider>
                                </ListingContextProvider>
                            </UserProvider>
                        </CartContextProvider>
                    </GoogleOAuthProvider>
                </I18nextProvider>
            </SchemaProvider>
        </HelmetProvider>
    );
};

GlobalProviders.propTypes = {
    children: PropTypes.node.isRequired,
};


function App({ configurableProps }) {
    const { pathname } = useLocation();
    const { t, i18n } = useTranslation();

    // const configuredProps = useMemo(() => configurableProps || getProps(), [configurableProps]);
    const configuredProps=configurableProps
    const { data: backendConfig, loading: backendConfigLoading } = useQuery(GET_BACKEND_CONFIG);
    const config = useMemo(
        () => readBackendConfig(backendConfig, backendConfigLoading),
        [backendConfig, backendConfigLoading]
    );

    const showHeaderFooter = (!pathname.includes('checkout') || pathname.includes('success')) && !configuredProps;
    const showNewsletterBanner = !pathname.includes('account') && !pathname.includes('checkout');
    const isHealthCheck = pathname.includes('health-check');

    const {gtm_id, gtm_is_active} = readBackendConfig(
        backendConfig,
        backendConfigLoading,
    );
    const tagManagerArgs = {
        gtmId: gtm_id
    }
    if (gtm_is_active) {
        TagManager.initialize(tagManagerArgs)
    }
    if (isHealthCheck) {
        return <HealthCheckRoute />;
    }

    return (
        <>
            <ServiceWorkerRegistration />
            <GlobalProviders>

                <ThemeEditorState initial={deityGreenTheme}>
                    {(props) => (
                        <SimpleThemeProvider theme={props.theme}>
                            {showHeaderFooter && <Header />}
                            <HeadMetaTags />
                            <PreConnect />
                            <div style={{ margin: '0 auto', padding: '0 15px' }}>
                                {showNewsletterBanner && <NewsletterSlider />}
                                <ScrollToTop />
                                <OnlineStatus>{({ isOnline }) => !isOnline && <p>You are offline.</p>}</OnlineStatus>
                                <ErrorBoundary>
                                    <I18n>
                                        {(t) => (
                                            <PageTransitionWrapper showLoader={false}>
                                                <Routes>
                                                    <Route path="/cookies" element={<CookieDeclaration />} />
                                                    <Route path="/image/media/*" element={<Media />} />
                                                    <Route path="/media/*" element={<Media />} />
                                                    <Route path="/compare" element={<Compare/>}/>
                                                    <Route path="/catalogsearch/result" element={<SearchResults />} />
                                                    <Route path="/checkout/payment/success" element={<CheckoutPaymentSuccess />} />
                                                    <Route path="/checkout/payment/cancel" element={<CheckoutPaymentCancel />} />
                                                    <Route path="/404" element={<NotFoundPage />} />
                                                    <Route path="/health-check" element={<HealthCheckRoute />} />
                                                    <Route path="/akcije-i-popusti.html" element={<SalesAndDiscounts />} />
                                                    <Route path="/blog/" element={<Blog />} />
                                                    <Route path="/blog/tag/:tagId" element={<Blog />} />
                                                    <Route path="/blog/:blogId" element={<BlogDetails />} />
                                                    <Route path={`/${t('news.newsLink')}/`} element={<News />} />
                                                    <Route path={`/${t('news.newsLink')}/:newsId/`} element={<NewsDetails />} />
                                                    <Route path="/checkout/cart/" element={<Cart />} />
                                                    <Route path="/checkout/" element={<Checkout />} />
                                                    <Route path="/checkout/success" element={<CheckoutSuccess />} />
                                                <Route path="/customer/registration/" element={<Registration />} />
                                                <Route path="/customer/login" element={<Login />} />
                                                <Route path="/customer/delete" element={<Delete />} />
                                                <Route path="/customer/account/forgotpassword/" element={<RequestReset />} />
                                                <Route path="/customer/account/createpassword/" element={<ResetPassword />} />
                                                <Route
                                                    path="/productalert/add/:productId/:productUrl"
                                                    element={<ProductAlertSubscribe />}
                                                />
                                                <Route
                                                    path="/productalert/unsubscribe/stock/product/:productId/"
                                                    element={<ProductAlertUnsubscribe />}
                                                />
                                                <Route
                                                    path="/productalert/unsubscribe/stockAll/"
                                                    element={<ProductAlertUnsubscribeAll />}
                                                />
                                                <Route path="/customer/account/logoutSuccess/" element={<LogoutSuccess />} />
                                                <Route
                                                    path="/newsletter/subscriber/confirm/id/:confirmId/code/:confirmCode"
                                                    element={<SubscriberConfirm />}
                                                />
                                                <Route path="/wishlist/:sharingCode" element={<Wishlist />} />
                                                {/* PrivateRoute wymaga dostosowania do v6 */}
                                                <Route
                                                    path="/customer/account/*"
                                                    element={
                                                        <PrivateRoute>
                                                            <PersonalArea />
                                                        </PrivateRoute>
                                                    }
                                                />
                                                <Route path="*" element={<DynamicRoute trackerId={config.analytics_id} />} />
                                                </Routes>
                                            </PageTransitionWrapper>
                                        )}
                                    </I18n>
                                </ErrorBoundary>
                            </div>
                            {showHeaderFooter && <Footer />}
                            <CompareBlock/>
                        </SimpleThemeProvider>

                    )}
                </ThemeEditorState>
            </GlobalProviders>
        </>
    );
}

App.propTypes = {
    configurableProps: PropTypes.object,
};

export default App;